import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';
import type { CreateDeviceDto, IDevice, UpdateDeviceDto } from '@/types/production/device';

// Helper function to transform date strings to Date objects
const transformDates = <T extends { created_at?: string | Date; identified_at?: string | Date }>(
  obj: T,
): T => {
  if (!obj) return obj;
  const result = { ...obj };
  if (result.created_at && typeof result.created_at === 'string') {
    (result as any).created_at = new Date(result.created_at);
  }
  if (result.identified_at && typeof result.identified_at === 'string') {
    (result as any).identified_at = new Date(result.identified_at);
  }
  return result;
};

export interface FindManyDevicesParams {
  page?: number;
  limit?: number;
  serial?: string;
  model?: string;
}

export interface PaginatedDevicesResponse {
  docs: IDevice[];
  total: number;
  pages: number;
  limit: number;
}

export const deviceApi = {
  findMany: async (params?: FindManyDevicesParams): Promise<PaginatedDevicesResponse> => {
    const { data } = await api.get<ApiPaginatedResponse<IDevice>>('/production/devices', {
      params,
    });
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<IDevice> => {
    const { data } = await api.get<ApiResponse<IDevice>>(`/production/devices/${id}`);
    return transformDates(data.data);
  },

  findBySerial: async (serial: string): Promise<IDevice> => {
    const { data } = await api.get<ApiResponse<IDevice>>(`/production/devices/by-serial/${serial}`);
    return transformDates(data.data);
  },

  create: async (payload: CreateDeviceDto): Promise<IDevice> => {
    const { data } = await api.post<ApiResponse<IDevice>>('/production/devices', payload);
    return transformDates(data.data);
  },

  createBatch: async (devices: CreateDeviceDto[]): Promise<IDevice[]> => {
    const { data } = await api.post<ApiResponse<IDevice[]>>('/production/devices/batch', devices);
    return data.data.map(transformDates);
  },

  update: async (id: string, payload: UpdateDeviceDto): Promise<IDevice> => {
    const { data } = await api.patch<ApiResponse<IDevice>>(`/production/devices/${id}`, payload);
    return transformDates(data.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/production/devices/${id}`);
  },

  upsertOne: async (filter: Record<string, any>, payload: UpdateDeviceDto): Promise<IDevice> => {
    const { data } = await api.post<ApiResponse<IDevice>>('/production/devices/upsert', {
      filter,
      payload,
    });
    return transformDates(data.data);
  },
};
